import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import DeviceInfo from '@/shared/libs/device-info';
import dayjs from 'dayjs';
import {
  DISABLE_SUBTITLES,
  setAudio,
  setAudioTracks,
  setBuffering,
  setControlsEnabled,
  setCurrentEventOnTimeline,
  setCurrentLiveProgram,
  setDuration,
  setFallbackAudioTrack,
  setFallbackSubtitle,
  setGradualPercentProgress,
  setHead,
  setPlayerStatus,
  setProfile,
  setProfiles,
  setProgress,
  setSeekRequested,
  setSeeking,
  setSubtitle,
  setSubtitles,
  state,
  switchSubtitles,
} from '@/shared/components/player/store';
import { PlayerStatus } from '@/shared/components/player/enums';
import { ErrorCode, ProductType } from '@/shared/enums';
import { calcGradualPercentProgress, getTrackLanguage } from '@/shared/components/player/store-utils';
import { getCurrentProgramByPriority } from '@/shared/utils';

const BOOKMARK_INTERVAL = 2.5 * 60 * 1000;
const BOOKMARK_SEEK_INTERVAL = 1000;
const PROLONG_INTERVAL_SECONDS = 300;
const MAX_TEXT_DURATION = 4;

export default {
  data() {
    return {
      bufferingStartTime: 0,
      info: this.$services.resolve('info'),
    };
  },
  watch: {
    player: 'attachListeners',
  },
  methods: {
    onTime(currentTime) {
      this.throttleAdblock();
      this.throttleBookmark();
      this.throttleSession();
      this.throttleCurrentTime(currentTime);
      this.throttleUpdateGradualPercentProgress();
    },
    onTimeLive(currentTime) {
      const player = this.player;

      if (!currentTime || state.isSeeking) {
        return;
      }

      // todo: handle playing postrolls for live changing

      const currentProgram = state.currentLiveProgram;
      this.updateProduct();
      if (currentProgram?.since) {
        const timeShiftStart = player.getLiveStartUTC() || 0;
        const currentLiveStart = currentProgram.since / 1000;
        const timeShiftOffset = currentLiveStart - timeShiftStart;
        const progress = currentTime - timeShiftOffset;
        setProgress(progress);
        setHead(player.getDuration() - player.getLiveDelay() - timeShiftOffset);
      }
    },
    onTimeVod(currentTime) {
      if (!currentTime || state.isSeeking) {
        return;
      }

      /**
       * We need make some correction of currentTime when we use Safari browser.
       * See SOFAMB-17668
       * */
      const currentTimeCorrected = DeviceInfo.isSafari() || DeviceInfo.isMobileSafari() ? currentTime - 0.2 : currentTime;
      if (state.duration > 0 && currentTimeCorrected >= state.duration) {
        return this.onDurationExceeded();
      }

      setProgress(currentTime);
      setCurrentEventOnTimeline(currentTime);
    },
    onSeekRequest() {
      setSeekRequested(true);
    },
    onSeekStart() {
      setSeeking(true);
    },
    onSeekEnd() {
      if (state.seekRequested) {
        this.debounceBookmark();
      }

      setSeeking(false);
      setSeekRequested(false);
      this.updateGradualPercentProgress();
    },
    onPaused() {
      this.saveBookmark();
      this.$stats.pausePlaying(state.product, this.getStatsCurrentTime());
    },
    onProfile(time, profile, adaptationEnabled) {
      const { bandwidth } = profile;
      state.currentProfile = profile;
      this.$stats.bitrateMonitor.updateBitrates(bandwidth / 1000);

      if (!adaptationEnabled) {
        setProfile(profile);
      }
    },
    onError(error) {
      setPlayerStatus(PlayerStatus.ERROR);
      setControlsEnabled(false);

      if (error && error.code) {
        // Ignore 'Failed to load segment' error
        if (error.code === 401) {
          return;
        }

        switch (error.code) {
          case 200:
            this.showAlert(ErrorCode.NO_SUPPORTED_RENDERER);
            break;
          case 206:
            this.showAlert(ErrorCode.AUDIO_RENDERER_ERROR);
            break;
          case 208:
            if (this.hevcPlayback) {
              return this.onHevcError();
            }

            this.showAlert(ErrorCode.MEDIA_TYPE_NOT_SUPPORTED);
            break;
          case 220:
            this.showAlert(ErrorCode.PLAYER_LOAD_PLAYLIST_FAILED);
            break;
          case 400:
            this.showAlert(ErrorCode.PLAYER_LOAD_MOVIE_FAILED);
            break;
          case 504:
            if (this.hevcPlayback) {
              return this.onHevcError();
            }

            this.showAlert(ErrorCode.DRM_GENERATE_MEDIA_KEYS_FAILED);
            break;
          case 507:
            this.showAlert(ErrorCode.PLAYER_LOAD_LICENSE_FAILED);
            break;
          default:
            this.showAlert(ErrorCode.GENERIC);
            break;
        }

        this.$stats.errorWhilePlaying(this.product, this.getStatsCurrentTime(), error.code);

        this.destroy();
      }
    },
    onAudioTrackChanged(track) {
      const audio = state.options.audioTracks.find((audioTrack) => {
        return getTrackLanguage(audioTrack) === getTrackLanguage(state.settings.audio);
      }) || track;

      setAudio(audio);
    },
    onInitialCanplay() {
      const userLanguage = getTrackLanguage(state.settings.audio);
      const playerLanguage = getTrackLanguage(this.player.getCurrentAudioTrack());

      if (userLanguage !== playerLanguage) {
        setFallbackAudioTrack();
        setFallbackSubtitle();
      }
    },
    onSubtitle(subtitle) {
      if (subtitle.language === state.settings.subtitle.language) {
        setSubtitle(subtitle);
      } else {
        setSubtitle(DISABLE_SUBTITLES);
      }
    },
    onSubtitleDisabled() {
      switchSubtitles([]);
    },
    onSubtitleCueChanged(cue) {
      const text = cue.text.trim();
      clearTimeout(this.resetTimeoutId);

      if (text) {
        const lines = text.split('\n');
        const duration = (cue.endTime || (cue.startTime + MAX_TEXT_DURATION)) - cue.startTime;
        switchSubtitles(lines);
        this.resetTimeoutId = setTimeout(() => switchSubtitles([]), duration * 1000);
      } else {
        switchSubtitles([]);
      }
    },
    onPlayInterrupted() {
      this.player.play();
    },
    onMetadata() {
      const duration = this.player.getDuration();
      if (state.product.type !== ProductType.LIVE) {
        setDuration(duration);
      }
      this.$stats.startPlaying(state.product);
    },
    onBufferingStart() {
      this.bufferingStartTime = new Date().getTime();
      setBuffering(true);
    },
    onBufferingEnd() {
      setBuffering(false);
      if (this.bufferingStartTime) {
        const bufferingDuration = (new Date().getTime() - this.bufferingStartTime) / 1000;
        const minBufferingTime = (this.info.platformSettings?.minBufferingTimeMilliseconds || 200) / 1000;
        if (bufferingDuration >= minBufferingTime) {
          this.$stats.bufferingWhilePlaying(state.product, this.getStatsCurrentTime(), Math.round(bufferingDuration));
        }
        this.bufferingStartTime = 0;
      }
    },
    onCanplay() {
      setBuffering(false);
    },
    onPlaying() {
      setBuffering(false);
      setPlayerStatus(PlayerStatus.PLAYING);
      if (!this.bufferingStartTime) {
        this.$stats.play(state.product, this.getStatsCurrentTime());
      }
      setControlsEnabled(true);
      this.hideAlert();
    },
    getStatsCurrentTime() {
      const player = this.player;
      let currentTime = player.getCurrentTime();

      if (isNaN(currentTime)) {
        currentTime = this.$refs.video.currentTime;
      }

      if (state.product.type === ProductType.LIVE) {
        return Math.round((player.timeShift?.getStart() || 0) + currentTime);
      }

      return Math.round(currentTime);
    },
    attachListeners() {
      const player = this.player;

      if (!player) {
        return;
      }

      this.$stats.bitrateMonitor.reset();

      const adblockDetectionIntervalInSeconds = this.info.platformSettings.adblockDetectionInterval || 60;
      const prolongVideoSessionInterval = this.config.prolongVideoSessionInterval || PROLONG_INTERVAL_SECONDS;

      this.debounceBookmark = debounce(this.saveBookmark, BOOKMARK_SEEK_INTERVAL);
      this.throttleBookmark = throttle(this.saveBookmark, BOOKMARK_INTERVAL, { leading: false });
      this.throttleAdblock = throttle(() => this.$store.dispatch('adblock/check'), adblockDetectionIntervalInSeconds * 1000);
      this.throttleSession = throttle(this.prolongSession, prolongVideoSessionInterval * 1000, { leading: false });
      this.throttleCurrentTime = throttle(state.product.type === ProductType.LIVE ? this.onTimeLive : this.onTimeVod, 1000);
      this.throttleUpdateGradualPercentProgress = throttle(this.updateGradualPercentProgress, 1000);

      player.on('time', this.onTime);
      player.on('seekToRequest', this.onSeekRequest);
      player.on('seeking', this.onSeekStart);
      player.on('seeked', this.onSeekEnd);
      player.on('paused', this.onPaused);
      player.on('profile', this.onProfile);
      player.on('error', this.onError);
      player.on('subtitles', setSubtitles);
      player.on('profiles', setProfiles);
      player.on('audioTrackChanged', this.onAudioTrackChanged);
      player.on('audioTracks', setAudioTracks);
      player.once('canplay', this.onInitialCanplay);
      player.on('subtitle', this.onSubtitle);
      player.on('subtitleDisabled', this.onSubtitleDisabled);
      player.on('subtitleCueChanged', this.onSubtitleCueChanged);
      player.on('playInterrupted', this.onPlayInterrupted);
      player.on('metadata', this.onMetadata);
      player.on('waiting', this.onBufferingStart);
      player.on('waitingEnd', this.onBufferingEnd);
      player.on('canplay', this.onCanplay);
      player.on('playing', this.onPlaying);
    },
    updateGradualPercentProgress() {
      const newGradualPercentProgress = calcGradualPercentProgress(state.progress, state.duration);
      if (state.gradualPercentProgress === newGradualPercentProgress || newGradualPercentProgress > 100) {
        return;
      }

      this.$stats.videoProgress(state.product, { progress: newGradualPercentProgress });
      setGradualPercentProgress(newGradualPercentProgress);
    },
    updateProduct() {
      const player = this.player;

      if (!player.timeShift?.isEnabled()) {
        const currentProgram = getCurrentProgramByPriority({ product: state.product, currentTime: this.time.now });
        return setCurrentLiveProgram(currentProgram || state.product.currentProgram);
      }

      const playerCurrentTime = player.getCurrentTime();
      const startTime = player.timeShift.getStart();
      const liveDelay = this.info.platformSettings.liveDelay || 50;

      const currentTime = dayjs((playerCurrentTime + startTime + liveDelay) * 1000);
      const currentProgram = getCurrentProgramByPriority({ product: state.product, currentTime });

      if (currentProgram) {
        setCurrentLiveProgram(currentProgram);
      }
    },
  },
};
